<script setup>
import { useVuelidate } from "@vuelidate/core";
import { useVModel } from "@vueuse/core";
import { usePrimeVue } from "primevue/config";
import InputSwitch from "primevue/inputswitch";
import { computed, defineEmits, defineProps, unref } from "vue";
import { inputSwitchPt } from "@/components/program-manager/sessions/components/PromotionalSettings/style.js";
import {
  GENERAL_GROUP_VISA_SERVICE,
  SPAIN_GROUP_VISA_SERVICE,
  UMASS_SEMESTER_TRANSCRIPT_SERVICES,
  UMASS_TRANSCRIPT_SERVICES,
  UNIVERSIDAD_CONGRESSO_TRANSCRIPT_SERVICES,
} from "@/components/program-manager/sessions/constants";
import { convertCost } from "@/components/program-manager/sessions/utils";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import Tooltip from "@/components/shared/Tooltip/PrimeVueTooltip.vue";
import { centsToDollars } from "@/mixins/helpers";

defineOptions({
  name: "ProductsAvailableForAddOn",
});

const { config } = usePrimeVue();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
  products: {
    type: Array,
    default: () => [
      GENERAL_GROUP_VISA_SERVICE,
      SPAIN_GROUP_VISA_SERVICE,
      UMASS_TRANSCRIPT_SERVICES,
      UNIVERSIDAD_CONGRESSO_TRANSCRIPT_SERVICES,
      UMASS_SEMESTER_TRANSCRIPT_SERVICES,
    ],
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

const rules = computed(() => ({
  programSession: props.products.reduce((acc, val) => {
    acc[val.key] = {};
    return acc;
  }, {}),
}));

const v$ = useVuelidate(rules.value, programSession, {
  $registerAs: "ProductsAvailableForAddOn",
  $lazy: true,
});

const updateProgramSession = (val, miscProduct) => {
  const { price_in_cents, key } = miscProduct;
  const session = unref(programSession);
  if (val) {
    session[key] = {
      price_in_dollars: centsToDollars(price_in_cents),
    };
  } else {
    session[key] = undefined;
  }
};

const getMiscProduct = (miscProduct) => {
  const product = unref(programSession)[miscProduct.key];
  if (!Object.hasOwn(product, "selected_by_default")) {
    product.selected_by_default = false;
  }
  return product;
};

const showSelectedByDefault = (miscProduct) => {
  // The arg product is one of the products from the
  // array of products passed in (or defaulted) as
  // a prop.
  const product = unref(programSession)[miscProduct.key];
  return !!product && Object.hasOwn(miscProduct, "selected_by_default");
};

const updateSelectedByDefault = (val, miscProduct) => {
  unref(programSession)[miscProduct.key].selected_by_default = val;
};
</script>

<template>
  <ExpandingSection field-label="Product Available for Add-on">
    <template #content>
      <div class="grid grid-cols-2 gap-x-24 gap-y-4">
        <div v-for="product in props.products" :key="product.id">
          <div class="col-span-1">
            <BasicCheckbox
              v-model="programSession[product.key]"
              field-label-classes="text-capitalize"
              :vuelidate-instance="v$?.programSession[product.key]"
              :field-label="product.name"
              @update:model-value="(val) => updateProgramSession(val, product)"
            />
            <div
              :class="[
                {
                  'text-gray-400': !programSession[product.key],
                  'flex flex-row items-center gap-x-4': showSelectedByDefault(product) ,
                },
                'mt-3',
              ]"
            >
              {{ convertCost(centsToDollars(product.price_in_cents)) }}
              <div
                v-if="showSelectedByDefault(product)"
                class="flex flex-row items-center gap-x-2"
              >
                <InputSwitch
                  v-model="getMiscProduct(product).selected_by_default"
                  :pt="inputSwitchPt(config.pt.inputswitch.slider)"
                  @update:model-value="(val) => updateSelectedByDefault(val, product)"
                  class="flex-shrink-0"
                />
                <label
                  class="flex flex-row items-center gap-x-1 text-gray-900"
                  data-testid="session-selected-by-default-label">
                  Selected by default
                  <Tooltip
                    message="If enabled, this add-on will be pre-selected for students during application. If disabled, students must actively choose to add it."/>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ExpandingSection>
</template>
